/**
 * Non-React glue for the Shortlist Django navbar entry point.
 *
 * This duplicates some of the functionality of the useShortlist hook to show
 * the count badge, this duplication helps us to avoid depending on React.
 *
 * Responsibilities:
 *  - Render the shortlist count badge from `localStorage.shortlist.opks.length`.
 *  - Keep the badge in sync across tabs via the `storage` event (sitters open
 *    in new tabs, so the count can change without a navigation).
 *  - On click, lazy-load the React modal island and dispatch the custom open
 *    event it listens for.
 */
import {
  SHORTLIST_MODAL_OPEN_EVENT,
  SHORTLIST_STORAGE_KEY,
} from '@rover/components/src/shortlist/constants';

const BUTTON_SELECTOR = '.js-shortlist-navbar-button';
const BADGE_SELECTOR = '.js-shortlist-navbar-badge';

const getShortlistCount = (): number => {
  try {
    const raw = localStorage.getItem(SHORTLIST_STORAGE_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.opks) ? parsed.opks.length : 0;
  } catch {
    return 0;
  }
};

const updateBadges = (): void => {
  const count = getShortlistCount();
  for (const badge of document.querySelectorAll<HTMLElement>(BADGE_SELECTOR)) {
    badge.textContent = count > 0 ? String(count) : '';
    badge.hidden = count === 0;
  }
};

const dispatchOpen = (): void => {
  window.dispatchEvent(new CustomEvent(SHORTLIST_MODAL_OPEN_EVENT));
};

let modalLoaded = false;
const openModal = (): void => {
  if (modalLoaded) {
    dispatchOpen();
    return;
  }
  import('@rover/components/src/shortlist/mount')
    .then(() => {
      modalLoaded = true;
      dispatchOpen();
      return undefined;
    })
    .catch(() => {
      // Allow a retry on the next click if the chunk failed to load.
    });
};

// The authenticated header is re-rendered client-side from a Mustache template
// (app/js/Authenticated.js does `$('.js-anonymous-header').html(...)`), which
// destroys and recreates the button. So we must not bind to the button node
// directly — it would be discarded on that re-render. Instead delegate clicks
// from `document`, which works for both the server-rendered (anonymous) button
// and the Mustache-recreated (authenticated) one, regardless of timing.
document.addEventListener('click', (event) => {
  const { target } = event;
  if (target instanceof Element && target.closest(BUTTON_SELECTOR)) {
    openModal();
  }
});

// Cross-tab sync: another tab may add/remove sitters.
window.addEventListener('storage', (event) => {
  if (event.key === SHORTLIST_STORAGE_KEY) {
    updateBadges();
  }
});

// Paint server-rendered badges present at load.
updateBadges();

// The Mustache re-render recreates the badge empty (the count is intentionally
// client-side only, not in the template context), so repaint whenever the
// header's direct children change. `childList` without `subtree` is important:
// our own textContent/hidden writes mutate the badge's descendants, not the
// <ul>'s direct children, so this observer can't trigger itself into a loop.
document.querySelectorAll<HTMLElement>('.js-anonymous-header').forEach((header) => {
  new MutationObserver(updateBadges).observe(header, { childList: true });
});
